import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { AustinSiblingCallouts } from "./patio-covers.in.austin";
import hero from "@/assets/original/welder-aluminum.jpg";
import heroPicture from "@/assets/original/welder-aluminum.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/aluminum-patio-covers/in/austin";
const TITLE = "Aluminum Patio Covers in Austin, TX | Texas Made Shade";
const DESC = "Welded aluminum patio covers in Austin, TX — powder-coated frames, engineered for Hill Country wind, 20-year structural warranty. Fabricated in our Seguin shop, installed across Travis County.";

export const Route = createFileRoute("/aluminum-patio-covers/in/austin")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Welded aluminum patio cover frame fabricated for an Austin install",
      socialTitle: "Aluminum Patio Covers in Austin, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Aluminum Patio Covers in Austin, TX",
        breadcrumbName: "Aluminum Patio Covers in Austin",
        description: DESC,
        faqs: [
          { q: "Why aluminum over steel or wood in Austin?", a: "Aluminum doesn't rust, doesn't rot and weighs about a third of steel — easier to span wide Hill Country patios without intermediate posts. In Austin's heat-and-storm cycle aluminum is the only frame metal we build with." },
          { q: "Do aluminum patio covers get hot in Texas sun?", a: "The frame itself stays cooler than steel because we powder-coat in light colors and the alloy reflects heat. Under-cover air temps drop 15–25°F vs. an open Austin patio when paired with a Phifertex Plus or insulated top." },
          { q: "Will an aluminum patio cover dent in hail?", a: "Our extrusions are heavy-gauge structural aluminum, not residential gutter stock. Pea- to quarter-size hail (typical for Central Texas) doesn't dent them. Severe hail can mark powder coat — we touch up under warranty." },
          { q: "What colors do you powder-coat?", a: "Standard colors are bronze, white, sandstone, matte black and Texas tan. Custom RAL colors are available with a 2-week lead time at no extra charge for larger frames." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Aluminum Patio Covers in Austin, TX",
          serviceType: "Aluminum Patio Cover Fabrication & Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "Austin", containedInPlace: { "@type": "AdministrativeArea", name: "Travis County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Aluminum Patio Covers in Austin"
      breadcrumbs={[
        { label: "Patio Covers", to: "/patio-covers/in/austin" },
        { label: "Aluminum Patio Covers in Austin, TX" },
      ]}
      eyebrow="★ ALUMINUM PATIO COVERS · AUSTIN, TX ★"
      title="Welded Aluminum Patio Covers in Austin, TX"
      intro="Every patio cover we ship to Austin is welded from structural aluminum extrusion — not screwed-together kit parts. Powder-coated, engineered for Hill Country wind loads, and backed by a 20-year structural warranty."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Aluminum Fabricator · Serving Austin, TX",
        headline: "Aluminum Patio Cover Quote — Free On-Site Estimate",
        subhead: "Send measurements and photos. We'll quote frame size, fabric or solid top, and color in one written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Patio / Pergola Cover")}&notes=${encodeURIComponent("Aluminum patio cover — Austin, TX.")}`,
        primaryLabel: "Get My Aluminum Cover Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Structural-grade welded aluminum (not screw-together kits)",
        "Powder-coated in 5 standard colors + custom RAL",
        "Engineered for Hill Country wind exposure",
        "Spans up to 20 ft without intermediate posts",
        "20-year structural warranty",
        "Built in Seguin · installed across Austin",
      ]}
      body={[
        {
          heading: "Welded, Not Bolted Together",
          text: "Big-box kits arrive in a flat pack with self-tapping screws and aluminum sleeves. They rattle in the first Hill Country thunderstorm and the joints fail at the screw holes. We MIG-weld every joint in our Seguin shop, then powder-coat the finished frame in one piece so the welds are sealed against Austin humidity.",
        },
        {
          heading: "Built for Hill Country Wind",
          text: "We size posts, beams and footings for the wind exposure your lot actually sees — ridgeline homes in Lakeway and Spicewood get heavier sections than sheltered urban lots in Hyde Park or Mueller. Stamped drawings available for City of Austin permits or HOA submissions.",
          cta: { label: "Patio covers overview", href: "/patio-covers/in/austin" },
        },
        {
          heading: "Pair It with the Right Top",
          text: "An aluminum frame is half the cover. Pick a Sunbrella canvas top for soft shade, Phifertex Plus stretched panels for breeze + UV block, or insulated Snap-N-Lock roof for true heat rejection and rain protection. We size and recommend on the same quote.",
          cta: { label: "See insulated cover options", href: "/insulated-patio-covers/in/austin" },
        },
      ]}
      applications={[
        "Attached Patio Covers",
        "Freestanding Pavilions",
        "Carports & Side-Yard Covers",
        "Outdoor Kitchen Shade",
        "Commercial Awning Frames",
        "Replacement Pergola Frames",
      ]}
      faqs={[
        { q: "Why aluminum over wood or steel?", a: "Aluminum doesn't rust, doesn't rot, weighs about a third of steel and spans further without posts. In Austin's heat and storm cycle it's the only frame metal we build with." },
        { q: "Will it dent in hail?", a: "Our heavy-gauge structural extrusions handle typical Central Texas hail without denting. Severe hail may mark powder coat — covered under warranty." },
        { q: "Do you powder-coat in custom colors?", a: "Yes — bronze, white, sandstone, matte black and Texas tan are stocked. Custom RAL colors add about 2 weeks lead time." },
        { q: "How long does it last?", a: "20+ years on the frame, no refinishing required. Sunbrella tops typically run 8–12 years before re-cover." },
      ]}
      cityCallouts={[
        { to: "/austin", city: "Austin", anchorText: "All Texas Made Shade services in Austin", blurb: "See every shade, awning and welding service we offer to Austin." },
        { to: "/custom-fabrication", city: "Our Shop", anchorText: "Inside our welding & fabrication shop", blurb: "Every aluminum patio cover is welded in our Seguin facility before it ships to Austin." },
      ]}
      postCta={<AustinSiblingCallouts current={PATH} />}
    />
  );
}
